import React from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Copy, GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  copyConditionInGroups,
  createEmptyConditionGroup,
  moveConditionInGroups
} from '../utils/ruleConditions';

const BETWEEN_DESCRIPTIONS = {
  and: '需要满足全部条件组',
  or: '满足其中一个条件组即可'
};

const WITHIN_DESCRIPTIONS = {
  and: '满足条件组内全部条件',
  or: '满足条件组内其中一个条件'
};

const LogicToggle = ({ label, value, descriptions, onChange }) => (
  <div className="condition-logic-control">
    <span>{label}</span>
    <div className="condition-logic-toggle" role="group" aria-label={label}>
      {['and', 'or'].map(option => (
        <button
          key={option}
          type="button"
          className={value === option ? 'is-active' : ''}
          onClick={() => onChange(option)}
          title={descriptions[option]}
          aria-label={`${option.toUpperCase()}：${descriptions[option]}`}
          aria-pressed={value === option}
        >
          {option.toUpperCase()}
        </button>
      ))}
    </div>
  </div>
);

const SortableConditionCard = ({
  condition,
  conditionIndex,
  groupId,
  getConditionSummary,
  onCopy,
  onDelete,
  onEdit
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: condition._editorId });
  const summary = getConditionSummary(condition);

  return (
    <div
      ref={setNodeRef}
      className="condition-card"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1
      }}
    >
      <button
        type="button"
        className="condition-icon-button condition-drag-handle"
        aria-label={`拖拽条件 ${conditionIndex + 1}`}
        title="拖拽条件"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="condition-card-content"
        onClick={() => onEdit(groupId, condition._editorId)}
        aria-label={`编辑条件 ${conditionIndex + 1}`}
      >
        <span className="condition-card-title">
          <strong>{summary.title}</strong>
          <span>{summary.detail}</span>
        </span>
        <span className="condition-card-meta">{summary.meta}</span>
      </button>
      <div className="condition-card-actions">
        <button
          type="button"
          className="condition-icon-button"
          onClick={() => onEdit(groupId, condition._editorId)}
          aria-label="编辑条件"
          title="编辑条件"
        >
          <Pencil size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="condition-icon-button"
          onClick={() => onCopy(groupId, condition._editorId)}
          aria-label="复制条件"
          title="复制条件"
        >
          <Copy size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="condition-icon-button condition-delete-button"
          onClick={() => onDelete(groupId, condition._editorId)}
          aria-label="删除条件"
          title="删除条件"
        >
          <Trash2 size={15} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const ConditionGroup = ({
  group,
  groupIndex,
  groupCount,
  getConditionSummary,
  onAddCondition,
  onCopyCondition,
  onDeleteCondition,
  onDeleteGroup,
  onEditCondition,
  onLogicChange
}) => {
  const { isOver, setNodeRef } = useDroppable({ id: group._editorId });

  return (
    <section className="condition-group">
      <div className="condition-group-header">
        <strong>条件组 {groupIndex + 1}</strong>
        <LogicToggle
          label="组内关系"
          value={group.logic}
          descriptions={WITHIN_DESCRIPTIONS}
          onChange={(value) => onLogicChange(group._editorId, value)}
        />
        <div className="condition-group-actions">
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => onAddCondition(group._editorId)}
          >
            <Plus size={15} aria-hidden="true" />
            添加条件
          </button>
          <button
            type="button"
            className="condition-icon-button condition-delete-button"
            onClick={() => onDeleteGroup(group._editorId, groupIndex)}
            disabled={groupCount <= 1}
            aria-label="删除条件组"
            title="删除条件组"
          >
            <Trash2 size={15} aria-hidden="true" />
          </button>
        </div>
      </div>
      <div
        ref={setNodeRef}
        className={`condition-drop-zone ${isOver ? 'condition-drop-zone-active' : ''}`}
      >
        <SortableContext
          items={group.conditions.map(condition => condition._editorId)}
          strategy={verticalListSortingStrategy}
        >
          {group.conditions.length > 0 ? (
            group.conditions.map((condition, conditionIndex) => (
              <SortableConditionCard
                key={condition._editorId}
                condition={condition}
                conditionIndex={conditionIndex}
                groupId={group._editorId}
                getConditionSummary={getConditionSummary}
                onCopy={onCopyCondition}
                onDelete={onDeleteCondition}
                onEdit={onEditCondition}
              />
            ))
          ) : (
            <button
              type="button"
              className="condition-empty-drop-target"
              onClick={() => onAddCondition(group._editorId)}
            >
              拖拽条件到这里，或点击添加条件
            </button>
          )}
        </SortableContext>
      </div>
    </section>
  );
};

const ConditionGroupsEditor = ({
  groups,
  betweenLogic,
  onBetweenLogicChange,
  onGroupsChange,
  getConditionSummary,
  onAddCondition,
  onEditCondition
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const updateGroupLogic = (groupId, logic) => {
    onGroupsChange(groups.map(group => (
      group._editorId === groupId ? { ...group, logic } : group
    )));
  };

  const deleteCondition = (groupId, conditionId) => {
    onGroupsChange(groups.map(group => (
      group._editorId === groupId
        ? {
            ...group,
            conditions: group.conditions.filter(
              condition => condition._editorId !== conditionId
            )
          }
        : group
    )));
  };

  const deleteGroup = (groupId, groupIndex) => {
    const group = groups.find(item => item._editorId === groupId);
    if (!group || groups.length <= 1) return;
    if (
      group.conditions.length > 0
      && !window.confirm(`确定删除条件组 ${groupIndex + 1} 及其中全部条件吗？`)
    ) {
      return;
    }
    onGroupsChange(groups.filter(item => item._editorId !== groupId));
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over) return;
    const targetGroup = groups.find(group => (
      group._editorId === over.id
      || group.conditions.some(condition => condition._editorId === over.id)
    ));
    if (!targetGroup) return;

    onGroupsChange(moveConditionInGroups(
      groups,
      active.id,
      targetGroup._editorId,
      over.id === targetGroup._editorId ? null : over.id
    ));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragEnd={handleDragEnd}
    >
      <div className="condition-groups-editor">
        <div className="condition-groups-toolbar">
          <LogicToggle
            label="条件组之间"
            value={betweenLogic}
            descriptions={BETWEEN_DESCRIPTIONS}
            onChange={onBetweenLogicChange}
          />
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => onGroupsChange([...groups, createEmptyConditionGroup()])}
          >
            <Plus size={15} aria-hidden="true" />
            添加条件组
          </button>
        </div>
        {groups.map((group, groupIndex) => (
          <ConditionGroup
            key={group._editorId}
            group={group}
            groupIndex={groupIndex}
            groupCount={groups.length}
            getConditionSummary={getConditionSummary}
            onAddCondition={onAddCondition}
            onCopyCondition={(groupId, conditionId) => (
              onGroupsChange(copyConditionInGroups(groups, groupId, conditionId))
            )}
            onDeleteCondition={deleteCondition}
            onDeleteGroup={deleteGroup}
            onEditCondition={onEditCondition}
            onLogicChange={updateGroupLogic}
          />
        ))}
      </div>
    </DndContext>
  );
};

export default ConditionGroupsEditor;
